import * as Sentry from '@sentry/browser';
import { makeMultiplexedTransport } from '@sentry/core';
import { matchPath } from 'react-router-dom';
import { createEnvelope, stackParserFromStackParserOptions, supportsFetch } from '@sentry/utils';
import { getDefaultOptions, getEnvFromLocation } from '@vtblife/sentry';
import { BrowserClientOptions } from '@sentry/browser/types/client';
import { Route, MicroApp } from '@vtblife/layout-config/types';
import type { BaseTransportOptions, Transport } from '@sentry/types';

type SentryMatcher = Parameters<typeof makeMultiplexedTransport>[1];

const OriginalBrowserClient = Sentry.BrowserClient;

/**
 * Wraps a base transport factory so per-DSN transports created by
 * makeMultiplexedTransport use /_sentry/<projectId> on bank domains (ER-3980).
 * Upstream multiplexed transport always builds a direct ingest URL and ignores tunnel.
 */
function withTunnel<TO extends BaseTransportOptions>(
    createTransport: (options: TO) => Transport,
): (options: TO) => Transport {
    return (options) => {
        try {
            if (window.location.href.includes('rb.m2') || window.location.href.includes('m2.region.vtb')) {
                const parsedUrl = new URL(options.url, window.location.origin);
                const projectId = parsedUrl.pathname.match(/\/api\/([^/]+)\//)?.[1];
                const projectKey = parsedUrl.searchParams.get('sentry_key');

                if (projectId && projectKey) {
                    const tunnel = `/_sentry/${projectId}`;

                    if (tunnel) {
                        const transport = createTransport({ ...options, url: tunnel });
                        const dsn = `${parsedUrl.protocol}//${projectKey}@${parsedUrl.host}/${projectId}`;

                        return {
                            ...transport,
                            send(envelope) {
                                const [headers, items] = envelope;
                                // Always stamp the DSN that matches this tunnel target (root client may
                                // have omitted it, or stamped a different project DSN).
                                return transport.send(createEnvelope({ ...headers, dsn }, items));
                            },
                        };
                    }
                }
            }
        } catch {
            // relative tunnel URL or invalid — keep as-is (fallback transport)
        }

        return createTransport(options);
    };
}

// TODO: remove this BrowserClient override after microfrontend migration to updated @vtblife/sentry module
class SentryBrowserClientAdapter extends Sentry.BrowserClient {
    // @ts-expect-error
    constructor(overrideOptions: BrowserClientOptions) {
        const defaultIntegrations =
            Sentry.getCurrentHub().getStack()[0]?.client?.getOptions()?.integrations || Sentry.defaultIntegrations;

        const options = getDefaultOptions({
            ...overrideOptions,
            transport: supportsFetch() ? Sentry.makeFetchTransport : Sentry.makeXHRTransport,
            stackParser: stackParserFromStackParserOptions(Sentry.defaultStackParser),
            integrations: defaultIntegrations,
        });

        return new OriginalBrowserClient(options as BrowserClientOptions);
    }
}

function getSentryOptions({
    environment,
    routes,
    applications,
    currentRoleType,
}: {
    environment: string | undefined;
    routes: Route[];
    applications: MicroApp[];
    currentRoleType: string;
}): Sentry.BrowserOptions {
    let externalConfigurationOptions = {};
    try {
        externalConfigurationOptions = JSON.parse(process.env.SENTRY_CONFIGURATION_OPTIONS || '{}');
    } catch {
        // do nothing, just incorrect external options
    }

    /**
     * The function retrieves the Sentry DSN based on the current path and the application name.
     */
    const dsnFromFeature = ({ getEvent }: Parameters<SentryMatcher>[0]) => {
        const path = window.location.pathname;
        const event = getEvent();

        const requiredAppName =
            event?.tags?.applicationName ||
            routes.find((route) => {
                const isMatch = matchPath(path, { path: route.path, exact: route.exact });
                const isRoleAllowed = route.requiredRoleTypes
                    ? route.requiredRoleTypes?.some((role) => currentRoleType === role)
                    : true;

                return isMatch && isRoleAllowed;
            })?.applicationName;

        if (event?.tags && !event.tags.applicationName) {
            Sentry.captureMessage('Используйте тег `applicationName` для определения dsn приложения в root.', {
                level: 'warning',
                tags: {
                    applicationName: requiredAppName || 'root',
                },
            });
        }

        if (requiredAppName) {
            return applications.find((app) => app.applicationName === requiredAppName)?.logger?.sentryDsn || [];
        }

        return [];
    };

    const defaultOptions = getDefaultOptions({
        dsn: process.env.SENTRY_DSN,
        release: process.env.CI_COMMIT_SHORT_SHA,
        environment: environment || getEnvFromLocation(),
        enabled: process.env.SENTRY_ENABLED === 'true',
        transport: makeMultiplexedTransport(
            withTunnel(supportsFetch() ? Sentry.makeFetchTransport : Sentry.makeXHRTransport),
            dsnFromFeature,
        ),
        ...externalConfigurationOptions,
    });

    const isProd = environment === 'prod';

    //TODO up sentry pack
    const integrations = defaultOptions.integrations || [];
    integrations.push(
        new Sentry.Replay({
            maskAllText: isProd,
            maskAllInputs: isProd,
            networkDetailAllowUrls: isProd ? [] : [window.location.origin],
        }),
    );

    return {
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        ...defaultOptions,
        integrations,
    };
}

export function configureSentry(environment: string | undefined, extra?: Record<string, any>) {
    const applications = extra?.configStore?.microAppsConfig?.applications || [];
    const routes = extra?.bundleTreeStore?.bundleTree?.routes || [];
    const currentRoleType = extra?.userStore.user?.currentRoleType || [];
    const options = getSentryOptions({ environment, routes, applications, currentRoleType });

    Sentry.init(options);

    // @ts-expect-error remove this logic after microfrontend migration to updated @vtblife/sentry module
    Sentry.BrowserClient = SentryBrowserClientAdapter;
}
